import { useQueries } from "@tanstack/react-query";
import DataProtectionDPIAService from "src/modules/data-protection/apis/DataProtectionDPIAService";
import { useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import RiskAssessmentQuestionnaire from "./components/RiskAssessmentQuestionnaire";
import ImpactAssessmentQuestionnaire from "./components/ImpactAssessmentQuestionnaire";
import { mapQuestions } from "src/modules/data-protection/helpers/questionnaire";
import {
    helpers
} from '@smartintegrity/questionnaire';
import { getQuestionnaireProgress } from "../../helpers/questionnaireProgress";
import {
    focusAssessmentQuestionElement,
    hasAssessmentQuestionFocus
} from "../../helpers/assessmentQuestionFocus";

const RISK_ASSESSMENT_ID = "risk_assessment";
const IMPACT_ASSESSMENT_ID = "data_protection_impact_assessment";

/**
 * These questionnaires stay in one controller because the risk assessment
 * result unlocks the DPIA assessment.
 */
const PrivacyQuestionnaires = ({
    onValidSubmit,
    reportCaseId,
    showImpactAssessment,
    activeAssessment,
    onProgressChange,
    questionFocus
}) => {
    const { softwareId } = useParams();
    const [reportedProgress, setReportedProgress] = useState(null);

    const isImpactAssessment = activeAssessment === "dpia";
    const sectionsIds = [
        RISK_ASSESSMENT_ID,
        IMPACT_ASSESSMENT_ID
    ];

    const questionsQueries = useQueries({
        queries: sectionsIds.map((sectionId) => {
            const isImpactSection = sectionId === IMPACT_ASSESSMENT_ID;

            return {
                queryKey: ['data-protection-report-dpia-questions-list', sectionId],
                queryFn: async () => {
                    const service = DataProtectionDPIAService.getInstance();

                    const response = await service.questions(sectionId, {
                        softwareId: softwareId,
                        reportCase: reportCaseId,
                    });
                    
                    const mappedQuestions = mapQuestions(response, response);

                    return {
                        mappedQuestions: helpers.makeQuestionsReadyForQuestionnaire(
                            mappedQuestions,
                            false
                        ),
                        rawQuestions: mappedQuestions,
                        mappedAnswers: helpers.makePreviousAnswersReadyForQuestionnaire(
                            mappedQuestions,
                            mappedQuestions
                        )
                    }
                },
                enabled:
                    isImpactSection === isImpactAssessment &&
                    (!isImpactSection || !!showImpactAssessment),
                refetchOnWindowFocus: false
            }
        })
    });

    const risksAssessmentQuery = questionsQueries[0];
    const impactAssessmentQuery = questionsQueries[1];
    const activeQuery = isImpactAssessment
        ? impactAssessmentQuery
        : risksAssessmentQuery;

    const handleProgressChange = useCallback((progress) => {
        setReportedProgress(progress);
    }, []);

    /**
     * Falls back to the stored answers until the questionnaire itself reports
     * its counters, so the assessment card is never empty on first paint.
     */
    const progress =
        reportedProgress ||
        getQuestionnaireProgress(
            activeQuery?.data?.mappedQuestions,
            activeQuery?.data?.mappedAnswers
        );

    useEffect(() => {
        onProgressChange && onProgressChange(progress.percent);
    }, [onProgressChange, progress.percent]);

    useEffect(() => {
        if (!hasAssessmentQuestionFocus(questionFocus) || activeQuery.isFetching || !activeQuery.data) {
            return;
        }

        focusAssessmentQuestionElement(questionFocus, {
            delayMs: 500,
            questions: activeQuery.data?.mappedQuestions
        });
    }, [questionFocus, activeQuery.isFetching, activeQuery.data]);

    const riskQuestionnaire = (
        <RiskAssessmentQuestionnaire
            questionsQuery={risksAssessmentQuery}
            softwareId={softwareId}
            onValidSubmit={onValidSubmit}
            onProgressChange={handleProgressChange}
        />
    );

    const impactQuestionnaire = (
        <ImpactAssessmentQuestionnaire
            questionsQuery={impactAssessmentQuery}
            softwareId={softwareId}
            onValidSubmit={onValidSubmit}
            showImpactAssessment={showImpactAssessment}
            onProgressChange={handleProgressChange}
        />
    );

    return isImpactAssessment
        ? impactQuestionnaire
        : riskQuestionnaire;
};

export default PrivacyQuestionnaires;
